/**
 * HW Timeline — Version: 1.1.5
 * Sequential scroll story: one progress value P (0–1) from how far the items
 * list has moved through the viewport. Scroll range is compressed
 * (SCROLL_RANGE_SCALE) so the sequence can finish around mid-view instead of
 * only after the block nearly leaves the viewport. L = max(0, n − 1) segments.
 * Segment j fill = clamp(P·L − j, 0, 1). Item 0 reveal ramps with segment 0;
 * item i ≥ 1 jumps to full when segment i − 1 completes (P·L ≥ i). Rail
 * markers (first/last) stay for CSS. prefers-reduced-motion: all full.
 *
 * Sticky image column: sets `--hw-timeline-image-sticky-half` on the image col
 * (half the rendered image height) so CSS can pin the column with the image
 * vertically centered in the viewport on desktop.
 *
 * Performance (v1.1.4):
 * - Item elements cached in `itemEls` WeakMap at init; no querySelectorAll per frame.
 * - IntersectionObserver tracks viewport visibility; roots only processed during
 *   scroll when actually intersecting the viewport.
 * - shopify:section:load redundant force-init loop removed; initAll handles it.
 */

(function () {
  'use strict';

  const INIT = 'data-hw-timeline-scroll-init';
  const FIRST = 'data-hw-timeline-first';
  const LAST = 'data-hw-timeline-last';

  /** @type {Set<HTMLElement>} roots currently intersecting the viewport */
  const activeRoots = new Set();

  /** @type {Set<HTMLElement>} roots currently visible (intersecting) */
  const visibleRoots = new Set();

  /** @type {WeakMap<HTMLElement, ResizeObserver>} */
  const resizeObservers = new WeakMap();

  /** @type {WeakMap<HTMLElement, IntersectionObserver>} */
  const intersectionObservers = new WeakMap();

  /** @type {WeakMap<HTMLElement, HTMLElement[]>} cached item elements per root */
  const itemElsCache = new WeakMap();

  /** @type {WeakMap<HTMLElement, ResizeObserver>} */
  const imageStickyObservers = new WeakMap();

  /** @type {WeakMap<HTMLElement, AbortController>} */
  const imageStickyAbort = new WeakMap();

  /** @type {Set<HTMLElement>} */
  const imageStickyRoots = new Set();

  let scrollRaf = 0;
  let windowScrollBound = false;
  let stickyHalfRaf = 0;
  let windowResizeStickyBound = false;

  /**
   * P = (vh − top) / ((vh + height) * SCROLL_RANGE_SCALE). At 0.5, P hits 1
   * after half of a "classic" pass — timeline completes while the block is
   * still well in view. Lower = finish earlier; higher = later.
   */
  const SCROLL_RANGE_SCALE = 0.5;

  /**
   * @param {number} x
   * @returns {number}
   */
  function clamp01(x) {
    return Math.min(1, Math.max(0, x));
  }

  function scheduleScrollFlush() {
    if (scrollRaf) {
      return;
    }
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      for (const root of [...visibleRoots]) {
        if (!(root instanceof HTMLElement) || !document.contains(root)) {
          visibleRoots.delete(root);
          activeRoots.delete(root);
          continue;
        }
        updateTimelineProgress(root);
      }
    });
  }

  function ensureWindowScrollListener() {
    if (windowScrollBound) {
      return;
    }
    windowScrollBound = true;
    window.addEventListener('scroll', scheduleScrollFlush, { passive: true });
    window.addEventListener('resize', scheduleScrollFlush, { passive: true });
  }

  function maybeRemoveWindowScrollListener() {
    if (activeRoots.size > 0) {
      return;
    }
    if (windowScrollBound) {
      window.removeEventListener('scroll', scheduleScrollFlush);
      window.removeEventListener('resize', scheduleScrollFlush);
      windowScrollBound = false;
    }
  }

  function scheduleStickyHalfFlush() {
    if (stickyHalfRaf) {
      return;
    }
    stickyHalfRaf = requestAnimationFrame(() => {
      stickyHalfRaf = 0;
      for (const root of [...imageStickyRoots]) {
        if (!(root instanceof HTMLElement) || !document.contains(root)) {
          imageStickyRoots.delete(root);
          continue;
        }
        updateImageStickyHalf(root);
      }
    });
  }

  function ensureWindowResizeStickyListener() {
    if (windowResizeStickyBound) {
      return;
    }
    windowResizeStickyBound = true;
    window.addEventListener('resize', scheduleStickyHalfFlush, {
      passive: true,
    });
  }

  function maybeRemoveWindowResizeStickyListener() {
    if (imageStickyRoots.size > 0) {
      return;
    }
    if (windowResizeStickyBound) {
      window.removeEventListener('resize', scheduleStickyHalfFlush);
      windowResizeStickyBound = false;
    }
  }

  function isDesktopTimelineSticky() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 990px)').matches
    );
  }

  /**
   * Sets half the rendered image height for viewport-centered sticky `top`.
   *
   * @param {HTMLElement} root
   */
  function updateImageStickyHalf(root) {
    const col = root.querySelector('.hw-timeline__image-col');
    const img = root.querySelector('.hw-timeline__image');
    if (!(col instanceof HTMLElement)) {
      return;
    }
    if (!isDesktopTimelineSticky()) {
      col.style.removeProperty('--hw-timeline-image-sticky-half');
      return;
    }
    if (!(img instanceof HTMLElement)) {
      col.style.removeProperty('--hw-timeline-image-sticky-half');
      return;
    }
    const h = img.offsetHeight;
    if (h <= 0) {
      return;
    }
    col.style.setProperty(
      '--hw-timeline-image-sticky-half',
      `${Math.round(h / 2)}px`,
    );
  }

  /**
   * @param {HTMLElement} root
   */
  function bindImageSticky(root) {
    unbindImageSticky(root);

    const col = root.querySelector('.hw-timeline__image-col');
    const img = root.querySelector('.hw-timeline__image');
    if (!(col instanceof HTMLElement) || !(img instanceof HTMLElement)) {
      return;
    }

    imageStickyRoots.add(root);
    ensureWindowResizeStickyListener();

    const ro = new ResizeObserver(() => {
      updateImageStickyHalf(root);
    });
    imageStickyObservers.set(root, ro);
    ro.observe(img);
    ro.observe(col);

    const ac = new AbortController();
    imageStickyAbort.set(root, ac);
    img.addEventListener(
      'load',
      () => {
        updateImageStickyHalf(root);
      },
      { signal: ac.signal },
    );

    updateImageStickyHalf(root);
  }

  /**
   * @param {HTMLElement} root
   */
  function unbindImageSticky(root) {
    imageStickyRoots.delete(root);
    maybeRemoveWindowResizeStickyListener();

    const ac = imageStickyAbort.get(root);
    if (ac) {
      ac.abort();
      imageStickyAbort.delete(root);
    }

    const ro = imageStickyObservers.get(root);
    if (ro) {
      ro.disconnect();
      imageStickyObservers.delete(root);
    }

    const col = root.querySelector('.hw-timeline__image-col');
    if (col instanceof HTMLElement) {
      col.style.removeProperty('--hw-timeline-image-sticky-half');
    }
  }

  /**
   * 0 = anchor top at bottom of viewport; 1 = sequence complete (see
   * SCROLL_RANGE_SCALE). Anchor is `.hw-timeline__items` so progress follows
   * the step list, not the whole section (e.g. desktop image column).
   *
   * @param {Element} itemsRoot
   * @returns {number}
   */
  function getScrollProgress(itemsRoot) {
    const rect = itemsRoot.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    const denom = (vh + rect.height) * SCROLL_RANGE_SCALE;
    if (denom <= 0) {
      return 0;
    }
    return clamp01((vh - rect.top) / denom);
  }

  /**
   * @param {HTMLElement} root
   */
  function updateTimelineProgress(root) {
    if (root.getAttribute('data-hw-timeline-scroll-reveal') !== 'true') {
      return;
    }

    if (root.classList.contains('hw-timeline--reduced-motion')) {
      return;
    }

    const itemsRoot = root.querySelector('.hw-timeline__items');
    if (!itemsRoot) {
      return;
    }

    // Use cached item elements — populated at init, never re-queried per frame.
    const itemEls = itemElsCache.get(root);
    if (!itemEls || !itemEls.length) {
      return;
    }

    const n = itemEls.length;
    const L = Math.max(0, n - 1);
    const P = getScrollProgress(itemsRoot);

    if (L === 0) {
      const el0 = itemEls[0];
      if (el0 instanceof HTMLElement) {
        el0.style.setProperty('--hw-timeline-item-reveal', clamp01(P).toFixed(4));
        el0.style.setProperty('--hw-timeline-step-fill', '0');
      }
      return;
    }

    for (let i = 0; i < n; i += 1) {
      const el = itemEls[i];
      if (!(el instanceof HTMLElement)) {
        continue;
      }

      let reveal;
      if (i === 0) {
        reveal = clamp01(P * L);
      } else {
        reveal = P * L >= i - 1e-9 ? 1 : 0;
      }
      el.style.setProperty('--hw-timeline-item-reveal', reveal.toFixed(4));

      if (i < n - 1) {
        const segFill = clamp01(P * L - i);
        el.style.setProperty('--hw-timeline-step-fill', segFill.toFixed(4));
      } else {
        el.style.setProperty('--hw-timeline-step-fill', '0');
      }
    }
  }

  /**
   * Marks first/last `.hw-timeline__item` for stem/connector CSS.
   *
   * @param {HTMLElement} root
   */
  function setTimelineRailMarkers(root) {
    const itemsRoot = root.querySelector('.hw-timeline__items');
    if (!itemsRoot) {
      return;
    }

    const itemEls = itemsRoot.querySelectorAll('.hw-timeline__item');
    for (const el of itemEls) {
      el.removeAttribute(FIRST);
      el.removeAttribute(LAST);
    }

    if (!itemEls.length) {
      return;
    }

    const firstEl = itemEls.item(0);
    const lastEl = itemEls.item(itemEls.length - 1);
    if (!firstEl || !lastEl) {
      return;
    }

    firstEl.setAttribute(FIRST, 'true');
    lastEl.setAttribute(LAST, 'true');
  }

  /**
   * @param {HTMLElement} itemEl
   */
  function clearItemScrollVars(itemEl) {
    itemEl.style.removeProperty('--hw-timeline-step-fill');
    itemEl.style.removeProperty('--hw-timeline-item-reveal');
  }

  /**
   * @param {HTMLElement} root
   */
  function destroyTimelineScroll(root) {
    activeRoots.delete(root);
    visibleRoots.delete(root);
    maybeRemoveWindowScrollListener();

    const io = intersectionObservers.get(root);
    if (io) {
      io.disconnect();
      intersectionObservers.delete(root);
    }

    const ro = resizeObservers.get(root);
    if (ro) {
      ro.disconnect();
      resizeObservers.delete(root);
    }

    const cached = itemElsCache.get(root);
    if (cached) {
      for (const el of cached) {
        if (el instanceof HTMLElement) {
          clearItemScrollVars(el);
        }
      }
      itemElsCache.delete(root);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {{ force?: boolean }} [options]
   */
  function initTimeline(root, options) {
    const force = options && options.force === true;

    if (root.getAttribute('data-hw-timeline-scroll-reveal') !== 'true') {
      return;
    }

    if (root.getAttribute(INIT) === '1' && !force) {
      return;
    }

    const itemsRoot = root.querySelector('.hw-timeline__items');
    if (!itemsRoot) {
      return;
    }

    if (force) {
      destroyTimelineScroll(root);
      root.removeAttribute(INIT);
      root.classList.remove('hw-timeline--scroll-ready');
      root.classList.remove('hw-timeline--reduced-motion');
    }

    const itemNodeList = itemsRoot.querySelectorAll('.hw-timeline__item');
    if (!itemNodeList.length) {
      return;
    }

    // Cache item elements as a plain array — reused every scroll frame.
    const itemEls = Array.from(itemNodeList);
    itemElsCache.set(root, itemEls);

    root.setAttribute(INIT, '1');

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );

    if (reduceMotion.matches) {
      root.classList.add('hw-timeline--reduced-motion');
      for (const el of itemEls) {
        if (el instanceof HTMLElement) {
          clearItemScrollVars(el);
        }
      }
      root.classList.add('hw-timeline--scroll-ready');
      return;
    }

    activeRoots.add(root);
    ensureWindowScrollListener();

    // IntersectionObserver: only process scroll for roots in the viewport.
    if (!intersectionObservers.has(root)) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              visibleRoots.add(root);
            } else {
              visibleRoots.delete(root);
            }
          }
        },
        { rootMargin: '200px 0px 200px 0px' },
      );
      intersectionObservers.set(root, io);
      io.observe(root);
    }

    let ro = resizeObservers.get(root);
    if (!ro) {
      ro = new ResizeObserver(() => {
        updateTimelineProgress(root);
      });
      resizeObservers.set(root, ro);
      ro.observe(root);
    }

    root.classList.add('hw-timeline--scroll-ready');
    updateTimelineProgress(root);
  }

  /**
   * @param {ParentNode} [scope]
   */
  function initAll(scope) {
    const rootNode = scope == null ? document : scope;
    const timelines = rootNode.querySelectorAll('.hw-timeline');
    for (const node of timelines) {
      if (!(node instanceof HTMLElement)) {
        continue;
      }
      setTimelineRailMarkers(node);
      bindImageSticky(node);
      if (node.getAttribute('data-hw-timeline-scroll-reveal') === 'true') {
        initTimeline(node);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // initAll handles full init (markers, sticky, scroll) for all timelines in
  // the loaded section. No need for a separate force-init loop.
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    initAll(target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const allTimelines = target.querySelectorAll('.hw-timeline');
    for (const node of allTimelines) {
      if (node instanceof HTMLElement) {
        unbindImageSticky(node);
      }
    }
    const timelines = target.querySelectorAll(
      '.hw-timeline[data-hw-timeline-scroll-reveal="true"]',
    );
    for (const node of timelines) {
      if (node instanceof HTMLElement) {
        destroyTimelineScroll(node);
        node.removeAttribute(INIT);
        node.classList.remove('hw-timeline--scroll-ready');
        node.classList.remove('hw-timeline--reduced-motion');
      }
    }
  });
})();
